import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { removeSupabaseChannelByName, subscribePostgresChannel } from "@/lib/realtime-channel";
import { OFFLINE_CONNECTION_STATES } from "@/lib/uazapi-connection-state";
import { useAuth } from "./useAuth";
import { useWorkspace } from "./useWorkspace";

export type ReconnectProvider = "whatsapp_evolution" | "whatsapp_uazapi" | "instagram" | "messenger";

export interface IntegrationReconnectIssue {
  id: string;
  provider: ReconnectProvider;
  status: "disconnected" | "error" | "token_expired";
  label: string;
}

interface State {
  issues: IntegrationReconnectIssue[];
  isChecking: boolean;
}

const PROVIDERS: ReconnectProvider[] = ["whatsapp_evolution", "whatsapp_uazapi", "instagram", "messenger"];

function getWhatsAppIdentity(row: any): string {
  const cfg = (row?.config as Record<string, any>) || {};
  return String(
    cfg.phone_number ||
      cfg.phone ||
      cfg.number ||
      cfg.display_phone_number ||
      row.instance_identifier ||
      row.id ||
      "",
  )
    .replace(/\D/g, "")
    .trim() || String(row.instance_identifier || row.id || "").trim().toLowerCase();
}

function deriveLabel(row: any): string {
  const cfg = (row?.config as Record<string, any>) || {};
  if (row.provider === "whatsapp_evolution") {
    return cfg.phone_number || cfg.display_name || row.instance_identifier || "WhatsApp";
  }
  if (row.provider === "whatsapp_uazapi") {
    return cfg.phone_number || cfg.display_name || row.instance_identifier || "WhatsApp Business";
  }
  if (row.provider === "instagram") {
    return cfg.instagram_username ? `@${cfg.instagram_username}` : cfg.page_name || "Instagram";
  }
  if (row.provider === "messenger") {
    return cfg.page_name || "Messenger";
  }
  return row.instance_identifier || row.provider;
}

export function useIntegrationReconnectStatus() {
  const { user } = useAuth();
  const { workspaceId } = useWorkspace(user?.id);
  const [state, setState] = useState<State>({ issues: [], isChecking: true });

  const check = useCallback(async () => {
    if (!workspaceId) {
      setState({ issues: [], isChecking: false });
      return;
    }
    try {
      const { data, error } = await supabase
        .from("integrations")
        .select("id, provider, status, instance_identifier, config, token_expires_at")
        .eq("workspace_id", workspaceId)
        .in("provider", PROVIDERS);

      if (error) {
        setState((p) => ({ ...p, isChecking: false }));
        return;
      }

      const rows = (data || []) as any[];
      const activeUazapiIdentities = new Set(
        rows
          .filter((row) => row.provider === "whatsapp_uazapi" && row.status === "active")
          .map(getWhatsAppIdentity)
          .filter(Boolean),
      );
      const issues: IntegrationReconnectIssue[] = [];
      for (const row of rows) {
        if (!PROVIDERS.includes(row.provider)) continue;
        const wasReplacedByActiveUazapi =
          row.provider === "whatsapp_evolution" && activeUazapiIdentities.has(getWhatsAppIdentity(row));
        if (wasReplacedByActiveUazapi) continue;
        const isWhatsappOffline =
          (row.provider === "whatsapp_evolution" || row.provider === "whatsapp_uazapi") &&
          OFFLINE_CONNECTION_STATES.has(
            String((row.config as Record<string, any> | null)?.connection_state || "").toLowerCase(),
          );
        if (row.status === "disconnected" || row.status === "error" || isWhatsappOffline) {
          issues.push({
            id: row.id,
            provider: row.provider,
            status: row.status === "error" ? "error" : "disconnected",
            label: deriveLabel(row),
          });
          continue;
        }
        if (row.status === "active" && row.token_expires_at) {
          const exp = new Date(row.token_expires_at);
          if (!Number.isNaN(exp.getTime()) && exp < new Date()) {
            issues.push({
              id: row.id,
              provider: row.provider,
              status: "token_expired",
              label: deriveLabel(row),
            });
          }
        }
      }
      setState({ issues, isChecking: false });
    } catch {
      setState((p) => ({ ...p, isChecking: false }));
    }
  }, [workspaceId]);

  useEffect(() => {
    check();
    if (!workspaceId) return;

    // Realtime: reage imediatamente a alterações de status (ex.: webhook marca disconnected).
    const channelName = `integration-reconnect-${workspaceId}`;
    const channel = subscribePostgresChannel(
      channelName,
      {
        event: "*",
        schema: "public",
        table: "integrations",
        filter: `workspace_id=eq.${workspaceId}`,
      },
      () => check(),
    );

    // Re-check leve a cada 5 minutos como segurança extra.
    const interval = setInterval(check, 5 * 60 * 1000);

    return () => {
      removeSupabaseChannelByName(channelName);
      supabase.removeChannel(channel);
      clearInterval(interval);
    };
  }, [check, workspaceId]);

  return { ...state, refresh: check };
}
